import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ConnectCodeResponse,
  CreateReceiptResponse,
  LivesplitData,
  Order,
  PrepaidCardData,
  Voucher,
  Slot,
  SlotDates,
  ChargeItem,
  SubmitReviewResponse,
  TableNeeds,
  WaitlistOptions,
  WaitlistJoinRequest,
  WaitlistJoinResponse,
  WaitlistStatusResponse,
  WaitlistRespondResponse,
  GuestReservationResponse,
  GuestReservationTimesResponse,
  GuestReservationChange,
  WaitlistAnswer,
  PrebookInvite,
  PrebookJoinContact,
  MarketingInfo
} from '../models/Order';
import { from, Observable } from 'rxjs';
import { Receipt } from '../models/Receipt';
import {BamboraSession, BamboraTokenizedAuth, BamboraApplePayWalletSession, BamboraGooglePayWalletSession, SwishLink, VippsLink, VivaPaymentOrder} from '../models/PaymentModels';
import {first, flatMap, switchMap, tap} from 'rxjs/operators';
import { ProfileInfo } from '../models/Profile';
import { ConfigService } from './config.service';
import {Identity} from '../models/Identity';
import {Auth, User} from '@angular/fire/auth';
import * as packageJson from '../../../package.json';
import {LoyaltyProgram, LoyaltyTransactionResponse} from '../models/loyalty-program';
import {ReviewData} from '../models/FSConfig';

const { name, version } = packageJson;

type SplitItem = {
  orderKey: string;
  rid: string;
};

@Injectable({
  providedIn: 'root'
})
export class AuthorizedApiService {
  private identity?: Identity;

  constructor(
    private http: HttpClient,
    private firebaseAuth: Auth,
    private cfg: ConfigService,
  ) { }

  private getOptions(token: any): any {
    const APP_VERSION = `${name}-${version}`;
    // @ts-ignore
    const headers = new HttpHeaders({ Authorization: token, "Venue-Id": this.identity.venueId, "Guest-Client-Version": APP_VERSION });
    return { headers, observe: "body", responseType: 'json' };
  }

  private async getCurrentUserToken() {
    const user = await this.firebaseAuth.currentUser;
    if (user == null) {
      return null;
    }
    return user.getIdToken();
  }

  private postWithOptions<T>(url: string, body: object): Observable<T> {
    return from(this.getCurrentUserToken()).pipe(flatMap(token => {
      if (token != null) {
        const o = this.getOptions(token);
        return this.http.post<Order>(url, body, o) as unknown as Observable<T>;
      } else {
        console.error("Cant post, no logged in user");
        console.error(body);
        throw new Error("Något fel har inträffat. Var god att ladda om sidan.");
      }
    }));
  }

  public getOrder(tableCode?: string): Observable<Order> {
    const body = { action: "order", table: tableCode };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public addItemToOrder(itemId: string, comment: string|undefined, uid: string, postActionData?: any, sameAsRid?: string): Observable<Order> {
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, { action: "additem", item_id: itemId, comment, uid, post_action_data: postActionData, same_as_rid: sameAsRid });
  }

  public addItemsToOrder(itemIds: string[], parentRID: string|undefined, itemIncluded: boolean|undefined, itemMandatory: boolean|undefined): Observable<Order> {
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, { action: "additems", item_ids: itemIds, parent_rid: parentRID, item_included: itemIncluded, item_mandatory: itemMandatory });
  }

  public removeItemFromOrder(itemId: string, isPackage: boolean): Observable<Order> {
    const body = { action: "removeitem", item_id: itemId, is_package: isPackage };
    console.log("Removing item", body);
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public changeDT(key: string): Observable<Order> {
    const body = { action: "changedt", dt: key };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public changeTable(code: string|undefined, kiosk?: string): Observable<Order> {
    const body = { action: "changetable", code, kiosk};
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public prebookClaim(d: string, s: string): Observable<Order> {
    const body = { action: "prebook_claim", d, s };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public prebookInvite(email?: string): Observable<Order> {
    const body = { action: "prebook_invite", email };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  // The order state once the guest has joined (or is the booker), else what they are invited to
  public prebookJoin(token: string, contact?: PrebookJoinContact): Observable<Order & {invite?: PrebookInvite}> {
    const body = { action: "prebook_join", token, ...contact };
    return this.postWithOptions<Order & {invite?: PrebookInvite}>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public prebookParticipantDone(): Observable<Order> {
    const body = { action: "prebook_participant_done" };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public prebookParticipantReopen(): Observable<Order> {
    const body = { action: "prebook_participant_reopen" };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public leaveSmartTable(): Observable<Order> {
    const body = { action: "leavesmarttable"};
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public confirmTable(): Observable<Order> {
    const body = { action: "confirmed_table"};
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public addDiscountCode(code: string): Observable<Order> {
    const body = { action: "adddiscount", code };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public activateDiscountCode(code: string, receiptKey: string): Observable<LivesplitData> {
    const body = { action: "activatediscount", code, receipt_key: receiptKey };
    return this.postWithOptions<LivesplitData>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public activateReward(card_key: string, reward_id: string): Observable<Order> {
    const body = { action: "activate_reward", card_key, reward_id };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public activateRewardReceipt(card_key: string, reward_id: string, receipt_key: string): Observable<LivesplitData> {
    const body = { action: "activate_reward_receipt", card_key, reward_id, receipt_key };
    return this.postWithOptions<LivesplitData>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public createBamboraSession(tip: any, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string, chargeItems?: ChargeItem[], subType?: string, declineRoute?: string): Observable<BamboraSession> {
    const body = { action: "beginpay", tip, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs, charge_items: chargeItems, sub_type: subType, decline_route: declineRoute };
    return this.postWithOptions<BamboraSession>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public createBamboraApplePayWalletSession(tip: any, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string, chargeItems?: ChargeItem[], declineRoute?: string): Observable<BamboraApplePayWalletSession> {
    const body = { action: "bambora_create_wallet_session", walletname: "ApplePay", tip, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs, charge_items: chargeItems, decline_route: declineRoute };
    return this.postWithOptions<BamboraApplePayWalletSession>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public createBamboraGooglePayWalletSession(tip: any, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string, chargeItems?: ChargeItem[], declineRoute?: string): Observable<BamboraGooglePayWalletSession> {
    const body = { action: "bambora_create_wallet_session", walletname: "GooglePay", tip, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs, charge_items: chargeItems, decline_route: declineRoute };
    return this.postWithOptions<BamboraGooglePayWalletSession>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public createVivaPaymentOrder(tip: any, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string, chargeItems?: ChargeItem[], loc?: string): Observable<VivaPaymentOrder> {
    const body = { action: "create_viva_payment_order", tip, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs, charge_items: chargeItems, loc};
    return this.postWithOptions<VivaPaymentOrder>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public chargeSavedCard(tip: any, receiptKey?: string, itemRefs?: any[], declineRoute?: string, browserInfo?: {}, plKey?: string, payableRefs?: string, chargeItems?: ChargeItem[]): Observable<BamboraTokenizedAuth> {
    const body = { action: "chargecard", tip, receipt_key: receiptKey, refs: itemRefs, decline_route: declineRoute, browser_info: browserInfo, pl_key:plKey, payable_refs: payableRefs, charge_items: chargeItems };
    return this.postWithOptions<BamboraTokenizedAuth>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public swishLink(tip: any, callbackurl?: string, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string, chargeItems?: ChargeItem[], payerAlias?: string, saveNumber?: boolean): Observable<SwishLink> {
    const body = { action: "swishlink", tip, callbackurl, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs, charge_items: chargeItems, payer_alias: payerAlias, save_number: saveNumber };
    return this.postWithOptions<SwishLink>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public vippsLink(tip: any, callbackurl: string, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string, chargeItems?: ChargeItem[]): Observable<VippsLink> {
    const body = { action: "vippslink", tip, callbackurl, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs, charge_items: chargeItems };
    return this.postWithOptions<VippsLink>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public executeLivesplit(itemRefs?: any[]): Observable<LivesplitData> {
    const body = { action: "execute_livesplit", refs: itemRefs };
    return this.postWithOptions<LivesplitData>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public placeOrder(): Observable<Order> {
    const body = { action: "placeorder" };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public getReceipts(): Observable<Receipt[]> {
    const body = { action: "receipts_list" };
    return this.postWithOptions<Receipt[]>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public printVoucher(voucherSetKey: string, stationId: string): Observable<any> {
    const body = { action: "receipt_bump_voucher_set", voucher_set_key: voucherSetKey, station_id: stationId };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public livesplitData(receiptKey?: string, plKey?: string): Observable<LivesplitData> {
    const body = { action: "livesplit", receipt_key: receiptKey, pl_key:plKey };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public activateLivesplit(plKey?: string): Observable<LivesplitData> {
    const body = { action: "activate_livesplit", pl_key:plKey };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public updateCollected(phoneNumber?: string, time?: any, pickupLocation?: any, roomnumber?: any, dateKey?: string, namespace?: string, anonEmail?: string, genericData?: any, customId?: string, customValue?: any, customValues?: Record<string, any>, fulfillment?: string): Observable<Order> {
    const genericDataString = genericData ? JSON.stringify(genericData) : undefined;
    const body = { action: "update_collected", phone: phoneNumber, time, pickup_location: pickupLocation, roomnumber,
      date_key: dateKey, namespace, anon_email: anonEmail, generic_json: genericDataString, custom_id: customId, custom_value: customValue, custom_values: customValues, fulfillment };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public splitItems(itemsToSplit: SplitItem[], parts: number, plKey?: string): Observable<LivesplitData> {
    // Construct the body to include all items with their respective orderKey and rid
    const body = {
      action: "split_items",
      items: itemsToSplit.map(item => ({
        order_key: item.orderKey,
        rid: item.rid
      })),
      parts,
      pl_key: plKey
    };

    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public activateVoucherItem(voucherId?: number, voucherIds?: number[], vendorId?: string): Observable<Voucher> {
    const body = { action: "activate_voucher_item", voucher_id: voucherId, voucher_ids: voucherIds, vendor_id: vendorId };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public fetchPrepaidData(): Observable<PrepaidCardData> {
    const body = { action: "prepaid_data" };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public createRefillReceipt(itemId: string): Observable<CreateReceiptResponse> {
    const body = { action: "create_refill_receipt", item_id: itemId };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public createDispReceipt(itemId: string, ref: string): Observable<CreateReceiptResponse> {
    const body = { action: "create_disp_receipt", item_id: itemId, ref };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public payWithPrepaid(tip: any, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string): Observable<Order> {
    const body = { action: "pay_with_prepaid", tip, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public payWithAccount(tip: any, receiptKey?: string, itemRefs?: any[], plKey?: string, payableRefs?: string): Observable<Order> {
    const body = { action: "pay_with_account", tip, receipt_key: receiptKey, refs: itemRefs, pl_key:plKey, payable_refs: payableRefs };
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public payWithFree(tip: any, receiptKey?: string, itemRefs?: any[]): Observable<Order> {
    const body = { action: "pay_with_free", tip, receipt_key: receiptKey, refs: itemRefs};
    return this.postWithOptions<Order>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public getConnectCode(): Observable<ConnectCodeResponse> {
    const body = { action: "get_connect_code" };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public sendMessage(message: string): Observable<ConnectCodeResponse> {
    const body = { action: "send_message", message };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public callForWaiter(): Observable<ConnectCodeResponse> {
    const body = { action: "call_for_waiter" };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public fetchSlotDates(namespace: string): Observable<SlotDates> {
    const body = { action: "get_dates", namespace };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public fetchFreeSlots(dateKey: string, namespace: string): Observable<Slot[]> {
    const body = { action: "free_slots", date_key: dateKey, namespace };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  // samwise serves the waitlist and the guest's own reservation. A deep link (the SMS) can land before the anonymous
  // sign-in is done, so this waits for a signed-in user instead of failing like postWithOptions.
  private postSamwise<T>(path: string, venueId: string, body: object): Observable<T> {
    const signedIn = new Observable<User>(subscriber => this.firebaseAuth.onAuthStateChanged(user => {
      if (user != null) {
        subscriber.next(user);
      }
    }));
    return signedIn.pipe(
      first(),
      switchMap(user => from(user.getIdToken())),
      switchMap(token => {
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}`, "Venue-Id": venueId });
        return this.http.post<T>(`${this.cfg.samwise_url}/guest/${path}`, body, { headers });
      })
    );
  }

  // The needs the guest can tick for the booking type (none: the walk-in queue, any table): those the venue offers
  // and has tables for
  public getTableNeeds(venueId: string, bookingTypeId?: string): Observable<TableNeeds> {
    return this.postSamwise<TableNeeds>("table_needs", venueId, { booking_type_id: bookingTypeId });
  }

  // How a prebooking's join page asks about offers for the guest's contact info, and who they come from
  public marketingInfo(venueId: string, guestName: string, guestPhone: string, guestEmail: string): Observable<MarketingInfo> {
    const body = { guest_name: guestName, guest_phone: guestPhone, guest_email: guestEmail };
    return this.postSamwise<MarketingInfo>("marketing_info", venueId, body);
  }

  // The queue's length and the wait for a party of the size with the needs
  public waitlistOptions(venueId: string, partySize?: number, bookingTypeId?: string, needs: string[] = []): Observable<WaitlistOptions> {
    const body = { party_size: partySize, booking_type_id: bookingTypeId, needs };
    return this.postSamwise<WaitlistOptions>("waitlist_options", venueId, body);
  }

  public waitlistJoin(venueId: string, request: WaitlistJoinRequest): Observable<WaitlistJoinResponse> {
    return this.postSamwise<WaitlistJoinResponse>("waitlist_join", venueId, request);
  }

  public waitlistStatus(venueId: string, ref: string, token: string): Observable<WaitlistStatusResponse> {
    return this.postSamwise<WaitlistStatusResponse>("waitlist_status", venueId, { ref, token });
  }

  public waitlistRespond(venueId: string, ref: string, token: string, answer: WaitlistAnswer): Observable<WaitlistRespondResponse> {
    return this.postSamwise<WaitlistRespondResponse>("waitlist_respond", venueId, { ref, token, answer });
  }

  // The guest's own reservation behind the link in a reminder: ref and manage token
  public reservationStatus(venueId: string, ref: string, token: string): Observable<GuestReservationResponse> {
    return this.postSamwise<GuestReservationResponse>("reservation_status", venueId, { ref, token });
  }

  public reservationTimes(venueId: string, ref: string, token: string, dateKey: string, partySize: number): Observable<GuestReservationTimesResponse> {
    const body = { ref, token, date_key: dateKey, party_size: partySize };
    return this.postSamwise<GuestReservationTimesResponse>("reservation_times", venueId, body);
  }

  public reservationChange(venueId: string, ref: string, token: string, change: GuestReservationChange): Observable<GuestReservationResponse> {
    return this.postSamwise<GuestReservationResponse>("reservation_change", venueId, { ref, token, ...change });
  }

  public reservationCancel(venueId: string, ref: string, token: string): Observable<GuestReservationResponse> {
    return this.postSamwise<GuestReservationResponse>("reservation_cancel", venueId, { ref, token });
  }

  // The guest says they are coming ("Jag kommer"): the booking is confirmed
  public reservationConfirm(venueId: string, ref: string, token: string): Observable<GuestReservationResponse> {
    return this.postSamwise<GuestReservationResponse>("reservation_confirm", venueId, { ref, token });
  }

  // The guest's answer about offers on the page: yes to the text shown, or no thanks (objected)
  public reservationMarketing(venueId: string, ref: string, token: string, text: string,
                              objected: boolean): Observable<GuestReservationResponse> {
    return this.postSamwise<GuestReservationResponse>("reservation_marketing", venueId, { ref, token, text, objected });
  }

  setIdentity(identity: Identity) {
    this.identity = identity;
  }

  public getProfileInfo(): Observable<ProfileInfo> {
    const body = { action: "profile_info" };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public cleanEventHistory(): Observable<any> {
    const body = { action: "clean_event_history" };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public wipeProfile(): Observable<any> {
    const body = { action: "wipe_profile" };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public vivaResolveReceiptKey(orderCode: string, transactionId: string|undefined): Observable<any> {
    const body = { action: "viva_resolve_receipt_key", order_code: orderCode, transaction_id: transactionId };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public vivaCheckOrderStatus(orderCode: string, transactionId: string): Observable<any> {
    const body = { action: "viva_check_order_status", order_code: orderCode, transaction_id: transactionId };
    return this.postWithOptions<any>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public fetchLoyaltyPrograms(venueId: string): Observable<LoyaltyProgram[]> {
    const body = { action: "fetch_loyalty_cards"};
    return this.postWithOptions<LoyaltyProgram[]>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public connectToLoyaltyPrograms(identity: string): Observable<LoyaltyProgram[]> {
    const body = { action: "connect_loyalty_card", identity};
    return this.postWithOptions<LoyaltyProgram[]>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public unlinkLoyaltyCard(card_key: string): Observable<LoyaltyProgram[]> {
    const body = { action: "unlink_loyalty_card", card_key};
    return this.postWithOptions<LoyaltyProgram[]>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public signupLoyaltyCard(program_id: string, name: string, email: string, phone: string, birthday: string, acceptPromotion: boolean): Observable<LoyaltyProgram[]> {
    const body = { action: "signup_loyalty_card", program_id, name, email, phone, birthday, accept_promotion: acceptPromotion};
    return this.postWithOptions<LoyaltyProgram[]>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public fetchLoyaltyCardHistory(card_key: string): Observable<LoyaltyTransactionResponse> {
    const body = { action: "fetch_loyalty_card_history", card_key};
    return this.postWithOptions<LoyaltyTransactionResponse>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public fetchReview(receipt_key: string): Observable<ReviewData | null> {
    const body = { action: "fetch_review", receipt_key };
    return this.postWithOptions<ReviewData | null>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public createBookingReceipt(venueId: string, bookingId: string): Observable<{receipt_key: string}> {
    const body = { action: 'create_booking_receipt', venue_id: venueId, booking_id: bookingId };
    return this.postWithOptions<{receipt_key: string}>(`${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public submitReview(review: {
    date: string,
    type: 'qr_code' | 'self_service', rating: number,
    rating_food: number, rating_service: number,
    rating_ambiance: number, rating_value: number,
    receipt_key: string
  }): Observable<SubmitReviewResponse> {
    return this.postWithOptions<SubmitReviewResponse>(`${this.cfg.base_url}/api/mnu/exp`,
      { action: "submit_review", ...review });
  }

}
